import React, { useEffect, useState } from "react";
import { Button } from "../components/ui/button";
import { Link, useNavigate } from "react-router-dom";
import { ROUTES } from "../constants/routes";
import Loader from "../components/common/loader/loader";
import Error from "../components/common/error/error";
import { cn, notify } from "../lib/utils";
import { siteSettings } from "../settings/site-settings";
import TableHeaders from "../components/ui/table-headers";
import { TableColumn } from "../interfaces/tables";
import { MdContentCopy, MdDelete, MdEdit } from "react-icons/md";
import Table from "../components/ui/table";
import { PlusIcon } from "lucide-react";
import * as Tooltip from "@radix-ui/react-tooltip";
import usePageTitle from "../hooks/usePageTitle";
import { deleteSet, duplicateSetById, listAllSets } from "../api/admin/sets";
import DeleteSetDialog from "../components/sets/delete-set-dialog";
import { GoCheckCircleFill, GoXCircleFill } from "react-icons/go";

function Sets() {
  usePageTitle("Sets");

  const navigate = useNavigate();

  // fetch templates from server
  const [sets, setSets] = useState([]);
  const [tableData, setTableData] = React.useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // table headers state, search, sort, page.
  const [sort, setSort] = React.useState<any>({ createdAt: -1 });
  const [search, setSearch] = React.useState(null);

  const [page, setPage] = React.useState(1);

  const [pageSize, setPageSize] = React.useState(
    siteSettings.tables.sets.defaultPageSize.key
  );

  const [filters, setFilters] = React.useState<any>({}); // pre-made filters published/unpublished

  const [paginationTotalCount, setPaginationTotalCount] = React.useState(0);

  const [selectedSetForDelete, setSelectedSetForDelete] = React.useState<
    any | null
  >(null);

  const [setDeleteDialogOpen, setSetDeleteDialogOpen] = React.useState(false);

  const handleOpenDeleteSetDialog = (template: any) => {
    setSelectedSetForDelete(template);
    setSetDeleteDialogOpen(true);
  };

  const handleCloseDeleteSetDialog = () => {
    setSelectedSetForDelete(null);
    setSetDeleteDialogOpen(false);
  };

  const handleDuplicateSet = async (setId: string) => {
    // set loading to true
    setLoading(true);

    // Give browser control
    await new Promise((resolve) => setTimeout(resolve, 0));

    const response = await duplicateSetById(setId);

    // post request return 201 if successful
    if (!response || response.status !== 201) {
      notify({ type: "error", message: "Error duplicating set" });
      setLoading(false);
      return;
    }

    notify({
      type: "success",
      message: "Set duplicated successfully",
    });

    loadSets();
  };

  const loadSets = async () => {
    const response = await listAllSets({
      page,
      sort,
      search,
      pageSize,
      filters,
    });

    if (!response || response.status !== 200) {
      setError("Error loading templates");
      setLoading(false);
      return;
    }

    setSets(response.data?.data);
    setTableData(formatDataForTable(response.data?.data));
    setPaginationTotalCount(response.data?.total);
    setLoading(false);
    setError(null);
  };

  const handleOnSearchChange = (searchObj: any) => {
    if (!searchObj || !searchObj.value || searchObj.value === "") {
      setSearch(null);
      return;
    }

    setSearch(searchObj);
    setPage(1); // reset page to 1 when search changes
  };

  const handleOnSortChange = (sortObj: any) => {
    if (!sortObj) {
      setSort(null);
      return;
    }

    setSort(sortObj);
  };

  const handlePublishedFilterChange = (key: string) => {
    if (key === "all") {
      setFilters({});
    } else {
      setFilters({ isPublished: key === "published" ? true : false });
    }

    setPage(1); // reset page to 1 when filter changes
  };

  const handleOnPageSizeChange = (pageSize: number) => {
    setPageSize(pageSize);
    setPage(1);
  };

  useEffect(() => {
    loadSets();
  }, [page, sort, search, pageSize, filters]);

  const handleOnDeleteSet = async (setId: string) => {
    const response = await deleteSet(setId);

    if (!response || response.status !== 200) {
      notify({ type: "error", message: "Error deleting set" });
      return;
    }

    notify({
      type: "success",
      message: "Set deleted successfully",
    });

    handleCloseDeleteSetDialog();

    loadSets();
  };

  const extractTemplateNamesAndIdsFromTemplates = (templates: any) => {
    return templates.map((template: any) => {
      return { internalName: template.internalName, _id: template._id };
    });
  };

  const extractPreviewImagesWithLocationsFromTemplates = (
    templates: any,
    templateLocationLinks: any
  ) => {
    return templates.map((template: any) => {
      return {
        previewImage: template.previewImage,
        printLocation: templateLocationLinks.find(
          (obj: any) => obj.templateId === template._id
        ).printLocation,
      };
    });
  };

  const openTemplateInNewTab = (templateId: string) => {
    window.open(`${ROUTES.DESIGN}/${templateId}`, "_blank");
  };

  const columns: TableColumn[] = [
    {
      key: "previewImages",
      label: "Preview Images",
      render: (cell, row) => {
        if (cell && cell.length > 0) {
          return (
            <div className="flex gap-x-1">
              {cell.map((obj: any, index: number) => (
                <div
                  key={index}
                  className="group flex w-min hover:cursor-zoom-in relative"
                >
                  <img
                    src={obj.previewImage}
                    alt="category"
                    className="h-[50px] max-w-[50px] object-cover rounded-md"
                  />

                  <div className="opacity-0 group-hover:opacity-100 fixed top-1/2 left-1/2 -translate-y-1/2 -translate-x-1/2 bg-borderColor border border-white rounded w-[500px] h-[500px] transition-opacity duration-0 ease-in group-hover:duration-500 group-hover:ease-out pointer-events-none">
                    <div className="absolute font-bold text-lg left-[50%] translate-x-[-50%] -top-10 bg-borderColor border-white border px-2 rounded">
                      {obj.printLocation}
                    </div>

                    <img
                      src={obj.previewImage}
                      alt="category"
                      className="w-full h-full object-contain rounded-md"
                    />
                  </div>
                </div>
              ))}
            </div>
          );
        } else {
          return (
            <div className="w-[35px] h-[50px] bg-borderColor rounded-md flex items-center justify-center text-gray-500">
              N/A
            </div>
          );
        }
      },
    },

    { key: "_id", label: "Internal Id" },

    { key: "internalName", label: "Internal Name" },

    {
      key: "isPublished",
      label: "Published",

      render: (cell, row) => {
        return (
          <div className="flex items-center">
            {cell ? (
              <GoCheckCircleFill size={20} className="text-green-500" />
            ) : (
              <GoXCircleFill size={20} className="text-red-500" />
            )}
          </div>
        );
      },
    },

    { key: "slug", label: "Slug" },

    {
      key: "templateNamesAndIds",
      label: "Templates",
      render: (cell, row) => (
        <div className="w-full flex items-center">
          <span className="font-bold">({cell?.length || 0})</span>

          {cell &&
            cell.length > 0 &&
            cell.slice(0, 2).map((obj: any, index: number) => (
              <div
                key={index}
                title="Open in new tab"
                className={cn(
                  "text-sm w-max h-full hover:text-primary hover:cursor-pointer",
                  {
                    "ml-1": index < 2,
                  }
                )}
                onClick={() => {
                  openTemplateInNewTab(obj._id);
                }}
              >
                {obj.internalName}
                {index < 1 && cell.length > 1 && ","}
              </div>
            ))}

          {cell && cell.length > 2 && (
            <div className="ml-1 flex">
              <Tooltip.Provider>
                <Tooltip.Root>
                  <Tooltip.Trigger asChild>
                    <PlusIcon size={16} className="text-primary" />
                  </Tooltip.Trigger>

                  <Tooltip.Portal>
                    <Tooltip.Content
                      className="data-[state=delayed-open]:data-[side=top]:animate-slideDownAndFade data-[state=delayed-open]:data-[side=right]:animate-slideLeftAndFade data-[state=delayed-open]:data-[side=left]:animate-slideRightAndFade data-[state=delayed-open]:data-[side=bottom]:animate-slideUpAndFade select-none rounded-[4px] bg-black text-white px-[15px] py-[10px] text-[15px] leading-none shadow-[hsl(206_22%_7%_/_35%)_0px_10px_38px_-10px,_hsl(206_22%_7%_/_20%)_0px_10px_20px_-15px] will-change-[transform,opacity]"
                      sideOffset={5}
                    >
                      <div className="flex flex-col gap-y-2">
                        {cell.map((obj: any, index: number) => (
                          <div
                            key={index}
                            title="Open in new tab"
                            className="flex items-center gap-x-2 hover:text-primary hover:cursor-pointer"
                            onClick={() => {
                              openTemplateInNewTab(obj._id);
                            }}
                          >
                            - <span>{obj.internalName}</span>
                          </div>
                        ))}
                      </div>
                      <Tooltip.Arrow />
                    </Tooltip.Content>
                  </Tooltip.Portal>
                </Tooltip.Root>
              </Tooltip.Provider>
            </div>
          )}
        </div>
      ),
    },

    {
      key: "printLocations",
      label: "Print Locations",
      render: (cell, row) => (
        <div className="w-full flex items-center">
          <span className="font-bold">({cell?.length || 0})</span>

          {cell &&
            cell.length > 0 &&
            cell.slice(0, 2).map((printLocation: any, index: number) => (
              <div
                key={index}
                className={cn("text-sm w-max h-full ", {
                  "ml-1": index < 2,
                })}
              >
                {printLocation}
                {index < 1 && cell.length > 1 && ","}
              </div>
            ))}

          {cell && cell.length > 2 && (
            <div className="ml-1 flex">
              <Tooltip.Provider>
                <Tooltip.Root>
                  <Tooltip.Trigger asChild>
                    <PlusIcon size={16} className="text-primary" />
                  </Tooltip.Trigger>

                  <Tooltip.Portal>
                    <Tooltip.Content
                      className="data-[state=delayed-open]:data-[side=top]:animate-slideDownAndFade data-[state=delayed-open]:data-[side=right]:animate-slideLeftAndFade data-[state=delayed-open]:data-[side=left]:animate-slideRightAndFade data-[state=delayed-open]:data-[side=bottom]:animate-slideUpAndFade select-none rounded-[4px] bg-black text-white px-[15px] py-[10px] text-[15px] leading-none shadow-[hsl(206_22%_7%_/_35%)_0px_10px_38px_-10px,_hsl(206_22%_7%_/_20%)_0px_10px_20px_-15px] will-change-[transform,opacity]"
                      sideOffset={5}
                    >
                      <div className="flex flex-col gap-y-2">
                        {cell.map((printLocation: any, index: number) => (
                          <div
                            key={index}
                            className="flex items-center gap-x-2"
                          >
                            - <span>{printLocation}</span>
                          </div>
                        ))}
                      </div>
                      <Tooltip.Arrow />
                    </Tooltip.Content>
                  </Tooltip.Portal>
                </Tooltip.Root>
              </Tooltip.Provider>
            </div>
          )}
        </div>
      ),
    },

    {
      key: "createdAt",
      label: "Created At",
      render: (cell) => {
        return cell ? new Date(cell).toLocaleDateString() : "N/A";
      },
    },

    {
      key: "btn",
      label: "Actions",
      fixed: true,
      render: (cell, row) => {
        return (
          <div className="flex gap-x-4">
            <Link to={`${ROUTES.SET_FORM}/${row._id}`}>
              <MdEdit
                title="Edit"
                className="hover:text-primary cursor-pointer"
                size={24}
              />
            </Link>

            <MdContentCopy
              title="Clone"
              className="hover:text-primary cursor-pointer"
              size={22}
              onClick={() => {
                handleDuplicateSet(row._id);
              }}
            />

            <MdDelete
              title="Delete"
              className="hover:text-red-600 cursor-pointer"
              size={24}
              onClick={() => {
                handleOpenDeleteSetDialog(row.fullDoc);
              }}
            />
          </div>
        );
      },
    },
  ];

  const formatDataForTable = (data: any) => {
    return data.map((d: any) => {
      return {
        _id: d._id,

        previewImages: extractPreviewImagesWithLocationsFromTemplates(
          d.templates,
          d.templateLocationLinks
        ),

        internalName: d.internalName,
        isPublished: d.isPublished,

        slug: d.slug,

        templateNamesAndIds: extractTemplateNamesAndIdsFromTemplates(
          d.templates
        ),

        description: d.description,
        printLocations: d.printLocations,
        createdAt: d.createdAt,
        btn: d._id,
        fullDoc: d,
      };
    });
  };

  return (
    <div className="w-full h-full">
      {loading && <Loader pageLoader />}

      {!loading && error && <Error />}

      {!loading && !error && (
        <div className="w-[95%] mx-auto ">
          <div className="w-full mx-auto ">
            <h1 className="text-2xl font-bold text-primary">Sets</h1>
            {paginationTotalCount && paginationTotalCount > 0 && (
              <p className="text-bodyDark">Total: {paginationTotalCount}</p>
            )}
          </div>

          <div className="w-full mx-auto flex items-end justify-end">
            <div>
              <Button
                onClick={() => navigate(ROUTES.SET_FORM)}
                className="ml-auto"
              >
                Create Set
              </Button>
            </div>
          </div>

          <div className="w-full block  mx-auto my-10 pb-20">
            <TableHeaders
              searchByOptions={siteSettings.tables.sets.searchByOptions}
              onSearchChange={handleOnSearchChange}
              sortByOptions={siteSettings.tables.sets.sortByOptions}
              onSortChange={handleOnSortChange}
              page={page}
              onPageChange={setPage}
              lastPage={
                paginationTotalCount === 0
                  ? 1
                  : Math.ceil(paginationTotalCount / pageSize)
              }
              pageSizeOptions={siteSettings.tables.sets.pageSizeOptions}
              onPageSizeChange={handleOnPageSizeChange}
              defaultPageSize={siteSettings.tables.sets.defaultPageSize}
              showPublishedFilter={true}
              onPublishedFilterChange={handlePublishedFilterChange}
              currentPublishedFilterValue={
                filters.isPublished === true
                  ? "published"
                  : filters.isPublished === false
                  ? "unpublished"
                  : "all"
              }
            />

            <Table columns={columns} data={tableData} />

            <DeleteSetDialog
              set={selectedSetForDelete}
              open={setDeleteDialogOpen}
              onClose={handleCloseDeleteSetDialog}
              onSetDelete={(setId: string) => {
                handleOnDeleteSet(setId);
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
}

export default Sets;
